/**
 * Asigna el parentId a una lista de categorías según su ubicación en la estructura de categorización
 * @param categories Lista de categorías plana a la que se le asignará el parentId
 * @param categorizations
 * @returns Lista de categorías con el parentId asignado
 */
export const assignParentIdToCategories = (
  categories: ICategory[],
  categorizations: ICategorization[],
): ICategory[] => {
  // Crear una copia de las categorías para no modificar el original
  const result = [...categories]

  // Función recursiva para buscar y asignar parentId
  function findAndAssignParent(node: ICategorization) {
    // Si el nodo tiene hijos, buscar en ellos
    if (node.children && node.children.length > 0) {
      node.children.forEach(child => {
        // Para cada hijo, buscar si existe en nuestra lista de categorías
        const categoryIndex = result.findIndex(category => category.id === child.id)

        // Si encontramos la categoría, asignarle el parentId
        if (categoryIndex !== -1) {
          result[categoryIndex].parentId = node.id
        }

        // Seguir buscando recursivamente en los hijos de este hijo
        findAndAssignParent(child)
      })
    }
  }

  // Iniciar la búsqueda desde el nodo raíz
  categorizations.forEach(categorization => findAndAssignParent(categorization))

  return result
}

export const buildCategoryTree = (
  categories: ICategory[],
  categorizations: ICategorization[],
): ICategoryNode[] => {
  const categoryWithParents = assignParentIdToCategories(categories, categorizations)

  const map = new Map<number, ICategoryNode>()
  categoryWithParents.forEach(cat => {
    map.set(Number(cat.id), { id: cat.id, name: cat.name, children: [] })
  })

  const roots: ICategoryNode[] = []
  categoryWithParents.forEach(cat => {
    const node = map.get(Number(cat.id))!
    if (!cat.parentId) {
      // nodo raíz
      roots.push(node)
    } else {
      const parentNode = map.get(Number(cat.parentId))
      if (parentNode) {
        parentNode.children.push(node)
      }
      // si no existe el padre, simplemente se omite
    }
  })

  return roots
}

export const flattenCategoryTree = (
  nodes: ICategoryNode[],
  parentId?: number,
): ICategory[] => {
  const result: ICategory[] = []

  nodes.forEach(node => {
    result.push({ id: node.id, name: node.name, parentId })
    if (node.children && node.children.length > 0) {
      result.push(...flattenCategoryTree(node.children, Number(node.id)))
    }
  })

  return result
}
